const decoder = new TextDecoder();

function extensionToMime(extension) {
  const types = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml' };
  return types[String(extension || '').toLowerCase()] || 'application/octet-stream';
}

function bytesToDataURL(bytes, mime) {
  let binary = '';
  const chunkSize = 32768;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return `data:${mime};base64,${btoa(binary)}`;
}

function parseStoredZip(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const entries = new Map();
  let offset = 0;
  while (offset + 30 <= bytes.length) {
    if (view.getUint32(offset, true) !== 0x04034b50) break;
    const flags = view.getUint16(offset + 6, true);
    const method = view.getUint16(offset + 8, true);
    const compressedSize = view.getUint32(offset + 18, true);
    const nameLength = view.getUint16(offset + 26, true);
    const extraLength = view.getUint16(offset + 28, true);
    if (flags & 0x08) throw new Error('This ZIP uses data descriptors. Export a fresh backup from MIЯЯOЯ and try again.');
    if (method !== 0) throw new Error('This backup uses compression this release does not support.');
    const nameStart = offset + 30;
    const nameEnd = nameStart + nameLength;
    const dataStart = nameEnd + extraLength;
    const dataEnd = dataStart + compressedSize;
    entries.set(decoder.decode(bytes.slice(nameStart, nameEnd)), bytes.slice(dataStart, dataEnd));
    offset = dataEnd;
  }
  return entries;
}

function hydrateFile(entries, object, dataKey, pathKey, typeKey, description) {
  if (object[dataKey] || !object[pathKey]) return;
  const bytes = entries.get(object[pathKey]);
  if (!bytes) throw new Error(`Missing ${description}.`);
  object[dataKey] = bytesToDataURL(bytes, object[typeKey] || extensionToMime(object[pathKey].split('.').pop()));
  delete object[pathKey];
  delete object[typeKey];
}

export async function readMirrorBackup(file) {
  if (file.name.toLowerCase().endsWith('.json')) return JSON.parse(await file.text());
  const entries = parseStoredZip(await file.arrayBuffer());
  const stateBytes = entries.get('closet.json');
  if (!stateBytes) throw new Error('The MIЯЯOЯ backup is missing closet.json.');
  const state = JSON.parse(decoder.decode(stateBytes));

  for (const location of state.locations || []) {
    hydrateFile(entries, location, 'imageData', 'imagePath', 'imageType', `reference image for ${location.name}`);
    for (const zone of location.zones || []) {
      hydrateFile(entries, zone, 'imageData', 'imagePath', 'imageType', `reference image for ${zone.name}`);
      zone.rowImages = (zone.rowImages || []).map((entry, rowIndex) => {
        if (!entry || typeof entry === 'string') return typeof entry === 'string' ? entry : '';
        const bytes = entries.get(entry.path);
        if (!bytes) throw new Error(`Missing row ${rowIndex + 1} for ${zone.name}.`);
        return bytesToDataURL(bytes, entry.type || extensionToMime(entry.path.split('.').pop()));
      });
      for (const item of zone.items || []) {
        hydrateFile(entries, item, 'imageData', 'imagePath', 'imageType', `item image for ${item.label || item.id}`);
        hydrateFile(entries, item, 'backImageData', 'backImagePath', 'backImageType', `back image for ${item.label || item.id}`);
        hydrateFile(entries, item, 'tagImageData', 'tagImagePath', 'tagImageType', `tag image for ${item.label || item.id}`);
      }
    }
  }

  const mirror = state.mirror || {};
  hydrateFile(entries, mirror, 'subjectImageData', 'subjectImagePath', 'subjectImageType', 'Mirror subject image');
  mirror.views ||= {};
  for (const [viewName, entry] of Object.entries(mirror.viewPaths || {})) {
    const bytes = entry?.path ? entries.get(entry.path) : null;
    if (bytes) mirror.views[viewName] = bytesToDataURL(bytes, entry.type || extensionToMime(entry.path.split('.').pop()));
  }
  delete mirror.viewPaths;

  for (const look of mirror.outfits || []) {
    hydrateFile(entries, look, 'snapshotImageData', 'snapshotImagePath', 'snapshotImageType', `snapshot for ${look.name}`);
    hydrateFile(entries, look, 'subjectImageData', 'subjectImagePath', 'subjectImageType', `subject for ${look.name}`);
    for (const item of look.items || []) hydrateFile(entries, item, 'imageData', 'imagePath', 'imageType', `piece for ${look.name}`);
  }
  for (const entry of mirror.ootdHistory || []) {
    hydrateFile(entries, entry, 'photoData', 'photoPath', 'photoType', `fit history photo for ${entry.name || entry.id}`);
  }
  return state;
}

export function downloadState(state) {
  const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `mirror-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
